<template>
  <CreateBase :current-step="2" :title="$t('Tender details')" v-on="$listeners">
    <template #form>
      <Form @submit="$emit('change-step', 3)">
        <div class="specify-info-form modal-content__container">
          <div class="two-form-items two-form-items_narrow">
            <FormInput
              id="tender-title-input"
              :placeholder="$t('e.g. Windows and doors')"
              :label-text="$t('Title')"
              :value="tender.name"
              required
              @input="handleTenderChanging('name', $event)"
            />
            <DatePickerInput
              id="deadline-for-offers"
              :placeholder="$t('DD/MM/YY')"
              :min="minDeadlineDate"
              :max="maxDeadlineDate"
              required
              :label-text="$t('Deadline for offers')"
              :value="tender.offers_deadline_date"
              type="date"
              @input="handleTenderChanging('offers_deadline_date', $event)"
            />
          </div>

          <div class="formcell">
            <label for="responsible-member" class="label">
              {{ $t('Responsible for tender') }}
            </label>

            <ResponsibleUser
              id="responsible-member"
              :team="team"
              :user-id="
                tender.contact_person_responsible_user_id ||
                tender.contact_person_responsible_user.id
              "
              @select="handleTenderChanging('contact_person_responsible_user_id', $event)"
            />
          </div>

          <FormInput
            id="project-address-input"
            :placeholder="$t('Enter address')"
            :label-text="$t('Address')"
            :value="tender.address"
            required
            @input="handleTenderChanging('address', $event)"
          />

          <FormInput id="taxes-input" :label-text="$t('Taxes')">
            <template #input-container>
              <TagsTextarea
                :items="tender.taxes"
                :placeholder="$t('Enter taxes, separated by commas')"
                @update-items="handleTenderChanging('taxes', $event)"
              />
            </template>
          </FormInput>

          <div class="formcell spacex-8">
            <Button
              v-if="!showDescription && !tender.description"
              type="secondary"
              icon="plus"
              size="small"
              @click.native="showDescription = true"
            >
              {{ $t('Description') }}
            </Button>
            <Button
              v-if="!showBudget && !tender.public_budget && !tender.internal_budget"
              type="secondary"
              icon="plus"
              size="small"
              @click.native="showBudget = true"
            >
              {{ $t('Budget') }}
            </Button>
            <Button
              v-if="!showFiles && !get(tender, 'files.length')"
              type="secondary"
              icon="plus"
              size="small"
              @click.native="showFiles = true"
            >
              {{ $t('Files') }}
            </Button>
          </div>

          <FormTextArea
            v-if="showDescription || tender.description"
            id="tender-description"
            class="specify-info-form__description"
            :label-text="$t('Description')"
            :placeholder="
              $t(
                'e.g. We need Aluminum windows and Fireproof doors for the 1st building. Please check the attached drawings. Download files here: https://link...'
              )
            "
            :value="tender.description"
            @input="handleTenderChanging('description', $event)"
          />

          <div
            v-if="showBudget || tender.public_budget || tender.internal_budget"
            class="two-form-items"
          >
            <FormInput
              id="public-budget-input"
              :max-length="13"
              :placeholder="$t('Optional')"
              :value="tender.public_budget"
              type="custom-number"
              :label-text="`${$t('Public budget')} (${currencySymbols[project.currency]}) · ${$t(
                'Visible for contractors'
              )}`"
              @input="handleTenderChanging('public_budget', $event)"
            />
            <FormInput
              id="internal-budget-offers"
              :max-length="13"
              :placeholder="$t('Optional')"
              :value="tender.internal_budget"
              type="custom-number"
              :label-text="`${$t('Internal budget')} (${currencySymbols[project.currency]})`"
              @input="handleTenderChanging('internal_budget', $event)"
            />
          </div>

          <FileAttachment
            v-if="showFiles || get(tender, 'files.length')"
            :description="
              $t(
                'Upload files that contractors might need to offer you bids. Each file must be under 100 MB.'
              )
            "
            :allowed-formats="null"
            :custom-label="$t('Files')"
            :file-links="tender.files"
            :max-count="20"
            upload-inside
            @change-files="(files) => handleFilesChanged(files)"
          />
        </div>
        <div class="modal-navigation__container navigation-container">
          <div class="hr" />
          <div class="modal__navigation">
            <ProgressSteps
              :current="2"
              class="navigation__steps"
              :steps="TENDER_EDIT_STEPS"
              @go-to-step="$emit('change-step', $event)"
            />

            <div class="navigation__buttons">
              <Button
                action-type="button"
                type="secondary"
                class="mr-8"
                @click="$emit('change-step', 1)"
              >
                {{ $t('Back') }}
              </Button>
              <Button :loading="saving" action-type="submit">
                {{ $t('Next step') }}
              </Button>
            </div>
          </div>
        </div>
      </Form>
    </template>
  </CreateBase>
</template>

<script>
  import CreateBase from '@/containers/tenders/CreateBase';
  import Button from '@/components/ui-v2/Button';
  import Form from '@/components/common/Form';
  import FormInput from '@/components/ui/FormInput';
  import FormTextArea from '@/components/ui/FormTextArea';
  import { mapActions, mapGetters, mapMutations, mapState } from 'vuex';
  import ProgressSteps from '@/components/ui/ProgressSteps';
  import { currentDate, datePlus } from '@/utils/date/current-date';
  import ClickOutside from 'vue-click-outside';
  import ResponsibleUser from '@/components/tenders/ResponsibleUser';
  import DatePickerInput from '@/components/ui/DatePickerInput';
  import TagsTextarea from '@/components/ui/TagsTextarea';
  import TENDER_EDIT_STEPS from '@/utils/constants/tender-edit-steps';
  import FileAttachment from '@/components/projects/FileAttachment';
  import get from 'lodash.get';

  export default {
    name: 'SpecifyInfo',
    directives: { ClickOutside },
    components: {
      FileAttachment,
      CreateBase,
      Button,
      Form,
      FormInput,
      FormTextArea,
      ProgressSteps,
      ResponsibleUser,
      DatePickerInput,
      TagsTextarea,
    },
    data() {
      return {
        checked: true,
        saving: false,
        minDeadlineDate: currentDate(),
        maxDeadlineDate: datePlus(0, 6),
        showDescription: false,
        showBudget: false,
        showFiles: false,
        TENDER_EDIT_STEPS,
      };
    },
    computed: {
      ...mapState({
        project: (state) => state.project.projectInfo,
        tender: (state) => state.tenderEdit.tender,
        team: (state) => state.modal.context.team.map((team) => team.user),
        userId: (state) => state.auth.user.id,
        currencySymbols: (state) => state.global.currencySymbols,
      }),
      ...mapGetters('tenderEdit', ['editId']),
    },
    methods: {
      get,
      ...mapActions({
        close: 'modal/closeModal',
        deleteFile: 'tenderEdit/deleteFile',
      }),
      ...mapMutations('tenderEdit', ['changeTender']),
      handleFilesChanged(files) {
        this.changeTender({
          key: 'files',
          value: files,
        });
        this.changeTender({
          key: 'file_ids',
          value: files.map((file) => file.id),
        });
      },
      handleTenderChanging(key, value) {
        this.changeTender({
          key,
          value,
        });
      },
    },
  };
</script>

<style lang="scss" scoped>
  .specify-info-form {
    padding: 24px 20px;
    &__description {
      ::v-deep textarea {
        height: 132px;
      }
    }
  }

  .specify-info-form__checkbox {
    display: flex;
    align-items: center;
  }

  .two-form-items {
    display: grid;
    grid-column-gap: 12px;
    grid-template-columns: 1fr 1fr;
    align-items: self-end;
  }

  .two-form-items_narrow {
    grid-template-columns: 2.5fr 1fr;
  }

  .attach-files-label {
    display: flex;
    align-items: center;
  }
</style>
